import { Box, Button, Checkbox, FormControlLabel, Divider, styled, Typography } from "@mui/material";
import { Stack } from "@mui/material";
import { useState, Children, useContext } from "react";
import { useNavigate } from "react-router-dom";
import ExtraordinaryColorLogo from "../../assets/logos/ExtraordinaryColor.png";
import { CheckoutLoadingContext } from "../../context/CheckoutLoadingContext";

type CheckoutBaseProps = {
    title: string;
    onConfirm: () => void;
    children: React.ReactNode;
    hasInsufficientCoverageFunds?: boolean;
}

const ConfirmationControls = styled(Stack)(() => ({
    paddingTop: "12px",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
}))

const VerticalDivider = styled(Divider)(() => ({
    margin: "0 24px 0 24px"
}))

const ConfirmationError = styled(Typography)(() => ({
    color: "red",
    marginTop: "12px"
}))


/**
 * This component is used to display a skeleton of the checkout flow.
 * It is not intended to be used directly. You should wrap it in a specific checkout component that will handle
 * the composition of a specific checkout with specific data handling.
 * The /Standard directory contains premade checkout components and interfaces for instances where customisation is not required.
 * There are two children that should be passed in:
 * - Product Details
 * - Line Items
 * Any of these may be safely omitted if you do not want to display anything for that section.
 * @param title - The title of the checkout page
 * @param children - The children of the checkout page
 * @param onConfirm - The function to call when the user confirms the checkout
 * @returns 
 */
export const CheckoutBase = ({ title, children, onConfirm, hasInsufficientCoverageFunds }: CheckoutBaseProps) => {
    const navigate = useNavigate();
    const [hasDeclaredAuthorisation, setHasDeclaredAuthorisation] = useState(false);
    const [shouldDisplayConfirmationError, setShouldDisplayConfirmationError] = useState(false);
    const childArray = Children.toArray(children);
    const { setIsLoading } = useContext(CheckoutLoadingContext);

    const verifiedOnConfirm = () => {
        if (!hasDeclaredAuthorisation) {
            setShouldDisplayConfirmationError(true);
            return;
        }
        setIsLoading(true);
        setShouldDisplayConfirmationError(false);
        onConfirm();
        setTimeout(() => {
            setIsLoading(false);
        }, 3000);
    }

    const InsufficientFundsConfirmationBlock = () => {
        return (
            <>
                <Typography>You do not have adequate funds.</Typography>
                <Button variant={"contained"} onClick={() => navigate("/funding")}>Add Funds</Button>
            </>
        )
    }

    return (
        <Box>
            <Box textAlign={"center"} marginBottom={"24px"}>
                <img src={ExtraordinaryColorLogo} alt="Extraordinary Logo" style={{ width: "350px" }} />
                <Typography variant="h5" fontWeight={"bold"}>{title}</Typography>
            </Box>
            <Stack direction={"row"}>
                <Box flex={1}>
                    {childArray[0] ?? <></>}
                </Box>
                <VerticalDivider orientation="vertical" flexItem />
                <Box flex={1}>
                    {childArray[1] ?? <></>}
                    <ConfirmationControls direction={"row"}>
                        {hasInsufficientCoverageFunds ? 
                        <InsufficientFundsConfirmationBlock />
                        :
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={hasDeclaredAuthorisation}
                                            onChange={() => setHasDeclaredAuthorisation(prev => !prev)}
                                        />
                                    }
                                    label={<Typography fontSize={"14px"}>Please confirm that you are authorised to make this transaction</Typography>}
                                />
                                <Button variant={"contained"} onClick={verifiedOnConfirm} style={{textTransform: "none"}}>Continue</Button>
                            </>
                        }
                    </ConfirmationControls>
                </Box>
            </Stack>
            <Box textAlign={"right"}>
                {shouldDisplayConfirmationError && <ConfirmationError>You must declare your authority to make this transaction</ConfirmationError>}
            </Box>
        </Box>
    )
}